import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, LockKeyhole } from "lucide-react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { trackEvent } from "@/lib/analytics";

type InquiryFormProps = {
  kind: "general" | "vitality";
  heading?: string;
};

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function InquiryForm({ kind, heading }: InquiryFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const trackedStart = useRef(false);
  const generalMutation = trpc.inquiries.general.useMutation();
  const vitalityMutation = trpc.inquiries.vitality.useMutation();
  const [startedAt, setStartedAt] = useState(0);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [submittedName, setSubmittedName] = useState("");
  const [formError, setFormError] = useState("");

  const resetSecurityFields = () => {
    setStartedAt(Date.now());
    setIdempotencyKey(createIdempotencyKey());
  };

  useEffect(() => {
    resetSecurityFields();
  }, []);

  const isPending = generalMutation.isPending || vitalityMutation.isPending;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    const data = new FormData(event.currentTarget);
    const base = {
      firstName: String(data.get("firstName") ?? "").trim(),
      lastName: String(data.get("lastName") ?? "").trim(),
      phone: String(data.get("phone") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      website: String(data.get("website") ?? ""),
      startedAt,
      idempotencyKey,
    };

    try {
      if (kind === "general") {
        await generalMutation.mutateAsync({ ...base, comments: String(data.get("comments") ?? "").trim() });
      } else {
        await vitalityMutation.mutateAsync(base);
      }
      setSubmittedName(base.firstName);
      trackEvent("form_success", { kind });
      formRef.current?.reset();
      resetSecurityFields();
    } catch (error) {
      const message = error instanceof Error ? error.message : "We could not send your inquiry. Please call 913-322-4000.";
      setFormError(message);
    }
  }

  if (submittedName) {
    return (
      <div className="inquiry-success" role="status" aria-live="polite">
        <CheckCircle2 size={42} aria-hidden="true" />
        <p className="section-kicker">Inquiry received</p>
        <h2>Thank you, {submittedName}.</h2>
        <p>Your information has been received. Someone from the CORE office will follow up using the phone number or email you provided.</p>
        <div className="success-actions">
          <button type="button" onClick={() => setSubmittedName("")}>Send another inquiry</button>
          <Link href="/">Return home</Link>
        </div>
      </div>
    );
  }

  return (
    <form
      className="inquiry-form"
      ref={formRef}
      onSubmit={handleSubmit}
      onFocusCapture={() => {
        if (trackedStart.current) return;
        trackedStart.current = true;
        trackEvent("form_start", { kind });
      }}
      noValidate={false}
    >
      <div className="form-heading">
        <p className="section-kicker">{kind === "vitality" ? "Vitality inquiry" : "Start a conversation"}</p>
        <h2>{heading ?? (kind === "vitality" ? "Tell us how to reach you." : "How can we reach you?")}</h2>
        <p>{kind === "vitality" ? "Share four contact details. The office will call with class information and answer your questions." : "Share your contact details and, if helpful, a brief comment. The office will follow up personally."}</p>
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor={`${kind}-first-name`}>First name <span aria-hidden="true">*</span></label>
          <input id={`${kind}-first-name`} name="firstName" type="text" autoComplete="given-name" maxLength={100} required />
        </div>
        <div className="form-field">
          <label htmlFor={`${kind}-last-name`}>Last name <span aria-hidden="true">*</span></label>
          <input id={`${kind}-last-name`} name="lastName" type="text" autoComplete="family-name" maxLength={100} required />
        </div>
        <div className="form-field">
          <label htmlFor={`${kind}-phone`}>Phone <span aria-hidden="true">*</span></label>
          <input id={`${kind}-phone`} name="phone" type="tel" autoComplete="tel" inputMode="tel" minLength={7} maxLength={32} required />
        </div>
        <div className="form-field">
          <label htmlFor={`${kind}-email`}>Email <span aria-hidden="true">*</span></label>
          <input id={`${kind}-email`} name="email" type="email" autoComplete="email" maxLength={320} required />
        </div>
        {kind === "general" ? (
          <div className="form-field form-field-full">
            <label htmlFor="general-comments">Comments <span className="optional">Optional</span></label>
            <textarea id="general-comments" name="comments" rows={5} maxLength={2000} aria-describedby="comments-help" />
            <small id="comments-help">Please do not include private medical, insurance, payment, or urgent health information.</small>
          </div>
        ) : null}
      </div>

      <div className="website-field" aria-hidden="true">
        <label htmlFor={`${kind}-website`}>Website</label>
        <input id={`${kind}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      {formError ? <div className="form-error" role="alert">{formError} If the problem continues, call <a href="tel:+19133224000">913-322-4000</a>.</div> : null}

      <div className="form-submit-row">
        <button className="button-primary" type="submit" disabled={isPending || !startedAt || !idempotencyKey}>
          {isPending ? <><Loader2 className="form-spinner" size={18} /> Sending…</> : <>Submit inquiry <ArrowRight size={18} /></>}
        </button>
        <p><LockKeyhole size={14} aria-hidden="true" /> By submitting, you ask CORE Strategies PT to contact you about this inquiry. See the <Link href="/privacy">Privacy Policy</Link>.</p>
      </div>
    </form>
  );
}
